import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  templateService,
  TemplateFilters,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  DuplicateTemplateRequest,
} from '@services/template.service'

export function useTemplates(filters: TemplateFilters = {}) {
  return useQuery({
    queryKey: ['templates', filters],
    queryFn: () => templateService.fetchTemplates(filters),
  })
}

export function useTemplate(id: number) {
  return useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getTemplate(id),
    enabled: !!id,
  })
}

export function useCreateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) => templateService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useUpdateTemplate(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateTemplateRequest) => templateService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      queryClient.invalidateQueries({ queryKey: ['template', id] })
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => templateService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useDuplicateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DuplicateTemplateRequest }) =>
      templateService.duplicateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function usePreviewTemplate(id: number) {
  return useMutation({
    mutationFn: (variables: Record<string, string>) =>
      templateService.previewTemplate(id, variables),
  })
}

export function useSendTestEmail(id: number) {
  return useMutation({
    mutationFn: (data: { recipientEmail: string; variables: Record<string, string> }) =>
      templateService.sendTestEmail(id, data),
  })
}
